import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { FormValuesModel } from '.';
import { useFormikContext } from 'formik';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { INTAKE_FORM_FIELDS_GRID_SIZES } from 'constants/grids';
import {
  AmountOptions,
  CounterPartyOptions,
  SourceOfRevenueOptions,
  TimeframeOptions,
} from 'enums/onboarding/crm';
import {
  getPlaceholderForInputField,
  getPlaceholderForNumberField,
  getPlaceholderForSelect,
} from '../utils';

// components
import FieldIssue from '../FieldIssue';
import SubmitButtons from '../SubmitButtons';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import CounterPartySelect from 'components/Forms/FormComponents/SelectInputs/CRM/CounterPartySelect';
import TimeframeSelect, {
  IProps as TimeframeSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/TimeframeSelect';
import TrueFalseSelect, {
  IProps as TrueFalseSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/TrueFalseSelect';
import SourceOfFundingSelect, {
  IProps as SourceOfFundingSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/SourceOfFundingSelect';
import AccountUsePercentageSelect, {
  IProps as AccountUsePercentageSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/AccountUsePercentageSelect';
import ProjectedRevenueSelect, {
  IProps as ProjectedRevenueSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/ProjectedRevenueSelect';
import CountriesAutocomplete, {
  IProps as CountriesAutocompleteProps,
} from 'components/Forms/FormComponents/Autocompletes/CRM/CountriesAutocomplete';
import {
  Col,
  Row,
  Divider,
  FormField,
  FormInputAmount,
  FormInputAmountProps,
  FormInputTextArea,
  FormInputTextAreaProps,
  FormInputProps,
  FormInput,
} from '@ui';

const InnerForm = () => {
  const { t } = useTranslation('onboarding');
  const { values } = useFormikContext<FormValuesModel>();
  const { getActiveIssueByFieldId } = useEntityIntakeFormContext();

  const disabledParentFields = useMemo(() => {
    const initialDepositAmountFieldIssue = getActiveIssueByFieldId(
      'initialDepositAmount',
    );
    const intendToTransferFundsFrameDetailsFieldIssue = getActiveIssueByFieldId(
      'intendToTransferFundsFrameDetails',
    );

    const otherBanksFieldIssue = getActiveIssueByFieldId('otherBanks');

    const otherBankFeedbackFieldIssue =
      getActiveIssueByFieldId('otherBankFeedback');

    const isPrimaryAccountFieldIssue =
      getActiveIssueByFieldId('isPrimaryAccount');

    const accountUsePercentageFieldIssue = getActiveIssueByFieldId(
      'accountUsePercentage',
    );

    const plansToOpenExternalBankAccountsFieldIssue = getActiveIssueByFieldId(
      'plansToOpenExternalBankAccounts',
    );

    const plansToOpenExternalBankAccountsDetailsFieldIssue =
      getActiveIssueByFieldId('plansToOpenExternalBankAccountsDetails');

    const sourceOfFundsOtherDetailsFieldIssue = getActiveIssueByFieldId(
      'sourceOfFundsOtherDetails',
    );

    const thirdPartyNameFieldIssue = getActiveIssueByFieldId('thirdPartyName');

    const thirdPartyRelationshipFieldIssue = getActiveIssueByFieldId(
      'thirdPartyRelationship',
    );

    return {
      counterpartyType:
        thirdPartyNameFieldIssue?.isResolved === false ||
        thirdPartyRelationshipFieldIssue?.isResolved === false,
      initialDeposit: initialDepositAmountFieldIssue?.isResolved === false,
      sourceOfInitialDeposit:
        sourceOfFundsOtherDetailsFieldIssue?.isResolved === false,
      timeframeOfInitialDeposit:
        intendToTransferFundsFrameDetailsFieldIssue?.isResolved === false,
      holdAccountsAnywhereElse:
        otherBanksFieldIssue?.isResolved === false ||
        otherBankFeedbackFieldIssue?.isResolved === false ||
        isPrimaryAccountFieldIssue?.isResolved === false ||
        accountUsePercentageFieldIssue?.isResolved === false ||
        plansToOpenExternalBankAccountsFieldIssue?.isResolved === false ||
        plansToOpenExternalBankAccountsDetailsFieldIssue?.isResolved === false,
      isPrimaryBankAccount:
        plansToOpenExternalBankAccountsFieldIssue?.isResolved === false ||
        plansToOpenExternalBankAccountsDetailsFieldIssue?.isResolved === false,
      plansToOpenExternalBankAccounts:
        plansToOpenExternalBankAccountsDetailsFieldIssue?.isResolved === false,
    };
  }, [getActiveIssueByFieldId]);

  const shouldRenderSourceOfFundsOtherDetails = useMemo(() => {
    return values.sourceOfInitialDeposit.includes(SourceOfRevenueOptions.Other);
  }, [values.sourceOfInitialDeposit]);

  return (
    <>
      <Row wrap={true} justify="start">
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputProps>
            label={
              <FieldIssue
                fieldId="counterpartyType"
                isDisabledDueToChildComments={
                  disabledParentFields.counterpartyType
                }
              >
                {t('intake_form.funding.counterparty_name')}
              </FieldIssue>
            }
            disabled={disabledParentFields.counterpartyType}
            name="counterpartyType"
            component={CounterPartySelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        {values.counterpartyType == CounterPartyOptions.ThirdParty ? (
          <>
            <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
              <FormField<FormInputTextAreaProps>
                label={
                  <FieldIssue fieldId="thirdPartyName">
                    {t('intake_form.funding.third_party_name')}
                  </FieldIssue>
                }
                name="thirdPartyName"
                component={FormInput}
                additionalProps={{
                  renderPlaceholder: (validationRules) =>
                    getPlaceholderForInputField(validationRules, true, t),
                }}
              />
            </Col>
            <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
              <FormField<FormInputTextAreaProps>
                label={
                  <FieldIssue fieldId="thirdPartyRelationship">
                    {t('intake_form.funding.third_party_relationship')}
                  </FieldIssue>
                }
                name="thirdPartyRelationship"
                component={FormInputTextArea}
                additionalProps={{
                  rows: 5,
                  renderPlaceholder: (validationRules) =>
                    getPlaceholderForInputField(validationRules, true, t),
                }}
              />
            </Col>
          </>
        ) : null}
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<ProjectedRevenueSelectProps>
            label={
              <FieldIssue
                fieldId="initialDeposit"
                isDisabledDueToChildComments={
                  disabledParentFields.initialDeposit
                }
              >
                {t('intake_form.funding.initial_deposit')}
              </FieldIssue>
            }
            disabled={disabledParentFields.initialDeposit}
            name="initialDeposit"
            component={ProjectedRevenueSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        {values.initialDeposit === AmountOptions.Above1m ? (
          <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
            <FormField<FormInputAmountProps>
              label={
                <FieldIssue fieldId="initialDepositAmount">
                  {t('intake_form.funding.specify_amount')}
                </FieldIssue>
              }
              name="initialDepositAmount"
              component={StyledFormInputAmount}
              additionalProps={{
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForNumberField(validationRules, true, t),
              }}
            />
          </Col>
        ) : null}
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<SourceOfFundingSelectProps>
            label={
              <FieldIssue
                fieldId="sourceOfFunds"
                isDisabledDueToChildComments={
                  disabledParentFields.sourceOfInitialDeposit
                }
              >
                {t('intake_form.funding.source_of_initial_deposit')}
              </FieldIssue>
            }
            disabled={disabledParentFields.sourceOfInitialDeposit}
            name="sourceOfInitialDeposit"
            component={SourceOfFundingSelect}
            additionalProps={{
              mode: 'multiple',
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, true, t),
            }}
          />
        </Col>
        {shouldRenderSourceOfFundsOtherDetails ? (
          <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
            <FormField<FormInputTextAreaProps>
              label={
                <FieldIssue fieldId="sourceOfFundsOtherDetails">
                  {t(
                    'intake_form.funding.source_of_initial_deposit_other_details',
                  )}
                </FieldIssue>
              }
              name="sourceOfInitialDepositOtherDetails"
              component={FormInputTextArea}
              additionalProps={{
                rows: 5,
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForInputField(validationRules, true, t),
              }}
            />
          </Col>
        ) : null}
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<CountriesAutocompleteProps>
            label={
              <FieldIssue fieldId="initialDepositCountries">
                {t('intake_form.funding.country_of_initial_deposit')}
              </FieldIssue>
            }
            name="countryOfInitialDeposit"
            component={CountriesAutocomplete}
            additionalProps={{
              mode: 'multiple',
              initialValue: values.countryOfInitialDepositInitialOptions,
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, true, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<TimeframeSelectProps>
            label={
              <FieldIssue
                fieldId="intendToTransferFundsFrame"
                isDisabledDueToChildComments={
                  disabledParentFields.timeframeOfInitialDeposit
                }
              >
                {t('intake_form.funding.timeframe_of_initial_deposit')}
              </FieldIssue>
            }
            disabled={disabledParentFields.timeframeOfInitialDeposit}
            name="timeframeOfInitialDeposit"
            component={TimeframeSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        {values.timeframeOfInitialDeposit === TimeframeOptions.NotSure ? (
          <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
            <FormField<FormInputTextAreaProps>
              label={
                <FieldIssue fieldId="intendToTransferFundsFrameDetails">
                  {t('intake_form.funding.more_info_about_initial_deposit')}
                </FieldIssue>
              }
              name="timeframeOfInitialDepositInformation"
              component={FormInputTextArea}
              additionalProps={{
                rows: 5,
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForInputField(validationRules, true, t),
              }}
            />
          </Col>
        ) : null}
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<TrueFalseSelectProps>
            label={
              <FieldIssue
                fieldId="hasOtherBanks"
                isDisabledDueToChildComments={
                  disabledParentFields.holdAccountsAnywhereElse
                }
              >
                {t('intake_form.funding.hold_account_anywhere_else')}
              </FieldIssue>
            }
            disabled={disabledParentFields.holdAccountsAnywhereElse}
            name="holdAccountsAnywhereElse"
            component={TrueFalseSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        {values.holdAccountsAnywhereElse ? (
          <>
            <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
              <FormField<FormInputTextAreaProps>
                label={
                  <FieldIssue fieldId="otherBanks">
                    {t('intake_form.funding.other_bank_accounts_info')}
                  </FieldIssue>
                }
                name="otherBankAccountsInfo"
                component={FormInputTextArea}
                additionalProps={{
                  rows: 5,
                  renderPlaceholder: (validationRules) =>
                    getPlaceholderForInputField(validationRules, true, t),
                }}
              />
            </Col>
            <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
              <FormField<FormInputTextAreaProps>
                label={
                  <FieldIssue fieldId="otherBankFeedback">
                    {t('intake_form.funding.issues_with_other_banks')}
                  </FieldIssue>
                }
                name="issuesWithOtherBanks"
                component={FormInputTextArea}
                additionalProps={{
                  rows: 5,
                  renderPlaceholder: (validationRules) =>
                    getPlaceholderForInputField(validationRules, true, t),
                }}
              />
            </Col>
            <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
              <FormField<TrueFalseSelectProps>
                label={
                  <FieldIssue
                    fieldId="isPrimaryAccount"
                    isDisabledDueToChildComments={
                      disabledParentFields.isPrimaryBankAccount
                    }
                  >
                    {t('intake_form.funding.is_primary_bank_account')}
                  </FieldIssue>
                }
                disabled={disabledParentFields.isPrimaryBankAccount}
                name="isPrimaryBankAccount"
                component={TrueFalseSelect}
                additionalProps={{
                  renderPlaceholder: (validationRules) =>
                    getPlaceholderForSelect(validationRules, true, false, t),
                }}
              />
            </Col>
            <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
              <FormField<AccountUsePercentageSelectProps>
                label={
                  <FieldIssue fieldId="accountUsePercentage">
                    {t('intake_form.funding.account_use_percentage')}
                  </FieldIssue>
                }
                name="accountUsePercentage"
                component={AccountUsePercentageSelect}
                additionalProps={{
                  renderPlaceholder: (validationRules) =>
                    getPlaceholderForSelect(validationRules, true, false, t),
                }}
              />
            </Col>
            {values.isPrimaryBankAccount === false ? (
              <>
                <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
                  <FormField<TrueFalseSelectProps>
                    label={
                      <FieldIssue
                        fieldId="plansToOpenExternalBankAccounts"
                        isDisabledDueToChildComments={
                          disabledParentFields.plansToOpenExternalBankAccounts
                        }
                      >
                        {t(
                          'intake_form.funding.plans_to_open_external_accounts',
                        )}
                      </FieldIssue>
                    }
                    disabled={
                      disabledParentFields.plansToOpenExternalBankAccounts
                    }
                    name="plansToOpenExternalBankAccounts"
                    component={TrueFalseSelect}
                    additionalProps={{
                      renderPlaceholder: (validationRules) =>
                        getPlaceholderForSelect(
                          validationRules,
                          true,
                          false,
                          t,
                        ),
                    }}
                  />
                </Col>
                {values.plansToOpenExternalBankAccounts ? (
                  <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
                    <FormField<FormInputTextAreaProps>
                      label={
                        <FieldIssue fieldId="plansToOpenExternalBankAccountsDetails">
                          {t(
                            'intake_form.funding.plans_to_open_external_accounts_details',
                          )}
                        </FieldIssue>
                      }
                      name="plansToOpenExternalBankAccountsDetails"
                      component={FormInputTextArea}
                      additionalProps={{
                        rows: 5,
                        renderPlaceholder: (validationRules) =>
                          getPlaceholderForInputField(validationRules, true, t),
                      }}
                    />
                  </Col>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}
      </Row>
      <HideIfDisabledForm>
        <Divider />
        <SubmitButtons />
      </HideIfDisabledForm>
    </>
  );
};

const StyledFormInputAmount = styled(FormInputAmount)`
  width: 100%;
`;

export default InnerForm;
